import * as Sentry from "@sentry/browser";
import { Form, Input, Select, Spin, Tag } from "antd";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import "../styles/components/AdvancedSearchModal.css";
import { AxiosResponse } from "axios";
import React from "react";
import {
  Activity,
  Company,
  Customer,
  CustomerTransaction,
  NotificationRecord,
  Product,
  ProductCategory,
  RMCompanyActivity,
  RedemptionsRequest,
  Role,
  TransferRequest,
  User,
  LoanRequest,
  FixedIncomeRequest,
  FixedIncomePaymentInstruction,
  FixedIncomeRedemption,
} from "../helpers/shared-interfaces";
import { badValues, formatSnakeCase, request } from "../helpers/utilities";
import ModalCreator from "./ModalCreator";

const { Option } = Select;

export const ADVANCED_QUERY = `
query AdvancedQuery($model: String!, $fields: [AdvancedQueryInput!]!, $page:Int!, $numberPerPage:ID) {
  advancedQuery(model: $model, fields: $fields, page:$page, numberPerPage: $numberPerPage)
}
`;

export const QUERABLE_FIELDS = `
query QueryableFields($model: String!) {
  queryableFields(model: $model)
}
`;

export const GET_OPERATORS = `
query GetOperators {
  operators
}
`;

interface Props {
  onError: (message: string) => void;
  onData: (data: AdvancedSearchReturn, fields: IField[]) => void;
  showModal: boolean;
  toggleModal: () => void;
  triggerField?: string;
  model: string;
  pageSize?: number;
  page?: number;
  activeFields?: IField[];
}

export interface IField {
  name: string;
  hasBetween: boolean;
  hasOr?: boolean;
  value: string;
  betweenValue?: string;
  orValue?: string;
  operator: string;
}

export interface AdvancedSearchReturn {
  results:
  | Customer[]
  | Company[]
  | Role[]
  | Product[]
  | ProductCategory[]
  | TransferRequest[]
  | RedemptionsRequest[]
  | Activity[]
  | User[]
  | CustomerTransaction[]
  | RMCompanyActivity[]
  | NotificationRecord[]
  | LoanRequest[]
  | FixedIncomeRequest[]
  | FixedIncomePaymentInstruction[]
  | FixedIncomeRedemption[];
  count: number;
}

interface State {
  isLoading: boolean;
  operators: Record<string, unknown>;
  hasBetween: boolean;
  queryableFields: string[];
  selectedFields: IField[];
}

export default class AdvancedSearchModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      queryableFields: [],
      selectedFields: [],
      hasBetween: false,
      operators: {},
    };
  }

  loadSavedFilters = () => {
    const savedFields = localStorage.getItem(this.props.model);
    if (savedFields) {
      this.setState({
        selectedFields: JSON.parse(savedFields) as IField[],
      });
    }
  };

  async componentDidMount(): Promise<void> {
    this.loadSavedFilters();
    await this.getQueryableFields();
    await this.getOperators();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.triggerField !== this.props.triggerField) {
      this.filterSelectedFields();
    }
    if (prevProps.pageSize !== this.props.pageSize) {
      this.getAdvancedSearch();
    }

    if (this.props.page !== prevProps.page) {
      this.getAdvancedSearch();
    }

    if (prevProps.activeFields && prevProps.activeFields.length > 0 && this.props.activeFields && this.props.activeFields.length === 0) {
      this.setState({ selectedFields: [] }, () => {
        localStorage.removeItem(this.props.model);
      });
    }
  }

  filterSelectedFields = () => {
    const field = this.props.triggerField;
    this.setState(
      (prevState: State) => ({
        selectedFields: prevState.selectedFields.filter(
          (f) => f.name !== field,
        ),
      }),
      () => {
        this.getAdvancedSearch();
      },
    );
  };

  getQueryableFields = async () => {
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<any> = await request({
        query: QUERABLE_FIELDS,
        variables: {
          model: this.props.model,
        },
      });

      this.setState({
        queryableFields: response.data.data.queryableFields,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.onError("Failed to fetch queryable fields");
      Sentry.captureException(error);
    }
  };

  getOperators = async () => {
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<any> = await request({
        query: GET_OPERATORS,
      });

      this.setState({
        operators: response.data.data.operators,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.onError("Failed to fetch operators");
      Sentry.captureException(error);
    }
  };

  removeField = (field: string) => {
    const { selectedFields, queryableFields } = this.state;
    this.setState(
      {
        selectedFields: selectedFields.filter((f) => f.name !== field),
        queryableFields: [...queryableFields, field],
      },
      () => {
        localStorage.setItem(
          this.props.model,
          JSON.stringify(this.state.selectedFields),
        );
      },
    );
  };

  addNewField = (field: string) => {
    const { selectedFields, queryableFields } = this.state;
    const isFieldsInSelect = selectedFields.find((f) => f.name === field);
    if (!isFieldsInSelect) {
      const selectedField: IField = {
        name: field,
        hasBetween: false,
        value: "",
        operator: "",
      };
      this.setState({
        selectedFields: [...selectedFields, selectedField],
        queryableFields: queryableFields.filter((f) => f !== field),
      });
    }
  };

  unsetModal = () => {
    const showModal = this.props.showModal;
    this.setState({ hasBetween: false });
    showModal && this.props.toggleModal();
    this.getQueryableFields();
  };

  getValues = () => {
    const { selectedFields } = this.state;
    const result: {
      field: string;
      operator: string;
      values: string[];
    }[] = [];
    selectedFields?.forEach((field) => {
      const data = {
        field: field.name,
        operator: field.operator,
        values: [field.value],
      };
      if (field.hasBetween) {
        data.values.push(field.betweenValue as string);
      }
      result.push(data);
    });
    return result;
  };

  getAdvancedSearch = async (): Promise<void> => {
    type AdvancedSearchValueType = string | boolean | number | string[];
    interface Response {
      data: {
        advancedQuery: any;
      };
    }

    this.setState({ isLoading: true });
    const variableFields = this.getValues();

    if (variableFields.length === 0) return this.setState({ isLoading: false });

    try {
      const response: AxiosResponse<Response> = await request({
        query: ADVANCED_QUERY,
        variables: {
          model: this.props.model,
          fields: variableFields,
          page: this.props.page ? this.props.page : 1,
          numberPerPage: this.props.pageSize ? this.props.pageSize : 25,
        },
      });

      const data = response.data.data.advancedQuery;
      if (!data.hasOwnProperty("count") && !data.hasOwnProperty("results")) {
        this.setState({ isLoading: false });
        this.props.onData(
          { count: 0, results: [] } as unknown as AdvancedSearchReturn,
          this.state.selectedFields,
        );
        this.props.onError("Failed to execute query");
        Sentry.captureException(data?.[0]?.errors);
      } else {
        const records = data.results;
        const validData = records.every(
          (item: any) => Object.keys(item).length > 1,
        );

        if (!validData) {
          console.log(records);
          console.log(validData);
          this.props.onError("Failed to execute query: invalid data");
          return;
        }

        this.props.onData(
          data as unknown as AdvancedSearchReturn,
          this.state.selectedFields,
        );
      }
      this.setState({ isLoading: false });
      this.unsetModal();
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.onError("Failed to execute query");
      Sentry.captureException(error);
    }
    localStorage.setItem(
      this.props.model,
      JSON.stringify(this.state.selectedFields),
    );
  };

  handleOnOperatorSelect = (field: string, operator: string) => {
    const { selectedFields } = this.state;
    let obj: any;

    const oldselectedFields = selectedFields;
    let existingSelectedFields = selectedFields.find(
      (item) => item.name === field,
    );
    if (existingSelectedFields !== undefined) {
      if (operator === "BETWEEN") {
        obj = {
          ...existingSelectedFields,
          hasBetween: true,
          hasOr: false,
          operator: "BETWEEN",
        } as IField;
      } else if (operator === "OR") {
        obj = {
          ...existingSelectedFields,
          hasBetween: false,
          hasOr: true,
          operator: operator,
        } as IField;
      } else {
        obj = {
          ...existingSelectedFields,
          operator: operator,
          hasBetween: false,
          hasOr: false,
        } as IField;
      }
    } else {
      obj = { name: field, operator: operator } as IField;
    }

    const modifiedselectedFields = oldselectedFields.map((fieldInfo) => {
      if (fieldInfo.name === obj.name) {
        return { ...obj } as IField;
      } else {
        return { ...fieldInfo };
      }
    });

    this.setState(
      {
        selectedFields: modifiedselectedFields as IField[],
      },
      () => {
        localStorage.setItem(
          this.props.model,
          JSON.stringify(this.state.selectedFields),
        );
      },
    );
  };

  handleOnFieldUpdate = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.persist();
    const { selectedFields } = this.state;
    const modifiedselectedFields = selectedFields.map((f) => {
      if (f.name === field) {
        return { ...f, value: e.target.value };
      }
      return f;
    });
    this.setState(
      {
        selectedFields: modifiedselectedFields,
      },
      () => {
        localStorage.setItem(
          this.props.model,
          JSON.stringify(this.state.selectedFields),
        );
      },
    );
  };

  handleOnBetweenUpdate = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.persist();
    const { selectedFields } = this.state;
    const modifiedselectedFields = selectedFields.map((f) => {
      if (f.name === field) {
        return { ...f, betweenValue: e.target.value };
      }
      return f;
    });
    this.setState(
      {
        selectedFields: modifiedselectedFields,
      },
      () => {
        localStorage.setItem(
          this.props.model,
          JSON.stringify(this.state.selectedFields),
        );
      },
    );
  };

  handleOnOrUpdate = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.persist();
    const { selectedFields } = this.state;
    const modifiedselectedFields = selectedFields.map((f) => {
      if (f.name === field) {
        return { ...f, orValue: e.target.value };
      }
      return f;
    });
    this.setState(
      {
        selectedFields: modifiedselectedFields,
      },
      () => {
        localStorage.setItem(
          this.props.model,
          JSON.stringify(this.state.selectedFields),
        );
      },
    );
  };

  sortFieldsByNameAsc = (fields: { label: string; value: string; }[]) => {
    return (
      fields.sort(function (a, b) {
        var textA = a.label.toUpperCase();
        var textB = b.label.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      }) || []
    );
  };

  render() {
    const { isLoading, selectedFields, operators, queryableFields } = this.state;
    const modifiedSelectFields = queryableFields?.map((field) => ({
      label: formatSnakeCase(field),
      value: field,
    }));
    const operatorOptions = Object.keys(operators).map((k) => ({
      value: operators[k] as string,
      label: formatSnakeCase(k as string),
    }));

    return (
      <>
        {this.props.showModal &&
          ModalCreator({
            title: "Advanced Search",
            okType: "primary",
            okText: "Apply Filters",
            isLoading: this.state.isLoading,
            onOkay: this.getAdvancedSearch,
            showModal: this.props.showModal,
            toggleModal: this.unsetModal,
            showFooter: true,
            width: 900,
            inscription: (
              <div className="asm-modal-container">
                <Spin spinning={isLoading}>
                  <div className="asm-field-selector-row">
                    <span className="asm-field-selector-label">Add search criteria</span>
                    <Select
                      key={selectedFields.length}
                      placeholder="Search and select field to add..."
                      optionFilterProp="children"
                      onChange={this.addNewField}
                      showSearch
                      style={{ width: "100%" }}
                      suffixIcon={<SearchOutlined />}
                      value={undefined}
                    >
                      {this.sortFieldsByNameAsc(modifiedSelectFields || []).map((f) => (
                        <Option key={f.value} value={f.value}>
                          {f.label}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <Form name="advancedSearchForm" layout="vertical">
                    <div className="asm-rules-container">
                      {selectedFields.length > 0 ? (
                        selectedFields.map((field) => (
                          <div className={`asm-rule-row ${field.hasBetween ? 'has-between' : ''}`} key={field.name}>
                            <div className="asm-rule-field-label">
                              <div className="asm-label-text">Field</div>
                              <div style={{ fontWeight: 600, fontSize: '14px', textTransform: 'capitalize' }}>
                                {formatSnakeCase(field.name)}
                              </div>
                            </div>

                            <div className="asm-rule-operator">
                              <Form.Item
                                style={{ marginBottom: 0 }}
                                label={<div className="asm-label-text">Operator</div>}
                              >
                                <Select
                                  options={operatorOptions}
                                  value={!badValues.includes(field.operator) ? field.operator : undefined}
                                  onChange={this.handleOnOperatorSelect.bind(null, field.name)}
                                  placeholder="Select Operator"
                                />
                              </Form.Item>
                            </div>

                            <div className="asm-rule-input">
                              <Form.Item
                                style={{ marginBottom: 0 }}
                                label={<div className="asm-label-text">Value</div>}
                              >
                                <Input
                                  defaultValue={field.value ?? undefined}
                                  onChange={this.handleOnFieldUpdate.bind(null, field.name)}
                                  placeholder={`Enter ${formatSnakeCase(field.name)}...`}
                                />
                              </Form.Item>
                            </div>

                            {field.hasBetween && (
                              <>
                                <div className="asm-rule-between-text">AND</div>
                                <div className="asm-rule-input">
                                  <Form.Item
                                    style={{ marginBottom: 0 }}
                                    label={<div className="asm-label-text">End Value</div>}
                                  >
                                    <Input
                                      defaultValue={field.betweenValue ?? undefined}
                                      onChange={this.handleOnBetweenUpdate.bind(null, field.name)}
                                      placeholder={`Enter ${formatSnakeCase(field.name)}...`}
                                    />
                                  </Form.Item>
                                </div>
                              </>
                            )}

                            <div className="asm-remove-btn" onClick={this.removeField.bind(null, field.name)}>
                              <CloseCircleOutlined style={{ fontSize: '18px' }} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="asm-empty-state">
                          No search criteria added yet. Select a field above to start.
                        </div>
                      )}
                    </div>
                  </Form>
                </Spin>
              </div>
            ),
          })}
      </>
    );
  }
}

interface SearchFieldProps {
  data: IField[];
  onClose?: (field: IField) => void;
}

export class AdvancedSearchFields extends React.Component<SearchFieldProps> {
  handleOnClose = (
    field: IField,
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e.preventDefault();
    this.props.onClose?.(field);
  };

  render() {
    return (
      <div style={{ marginLeft: "5px" }}>
        {this.props.data.map((field, index) => (
          <Tag
            key={index}
            id={`advanced-search-tag-${index}`}
            style={{ padding: "4px" }}
            closable={true}
            onClose={this.handleOnClose.bind(this, field)}
          >
            {`${formatSnakeCase(field.name)}: ${field.value?.toString() || "---"
              } `}
          </Tag>
        ))}
      </div>
    );
  }
}
